import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { adminGetMother, WHATSAPP_NUMBER } from '../../lib/api';
import { formatKL, formatKLShort } from '../../lib/timezone';
import RiskBadge from '../RiskBadge';
import { Copy, MessageCircle, User, Phone, Baby, ClipboardCheck, Droplet, Briefcase, Check } from 'lucide-react';
import { toast } from 'sonner';
import { buildWhatsAppSummary } from '../../lib/whatsappSummary';

export default function MotherDetailDrawer({ motherId, open, onClose, token, consultants = [] }) {
  const { lang, t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !motherId) return;
    setLoading(true);
    adminGetMother(motherId, token)
      .then(setData)
      .catch(() => toast.error(lang === 'zh' ? '加载失败' : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [motherId, open, token, lang]);

  if (!open) return null;
  const m = data?.mother;
  const subs = data?.submissions || [];
  const logs = data?.milk_logs || [];
  const cases = data?.cases || [];

  const copyLatestSummary = async () => {
    if (!subs[0]) return;
    const summary = buildWhatsAppSummary(subs[0], lang);
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      toast.success(lang === 'zh' ? '已复制' : 'Copied');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(lang === 'zh' ? '复制失败' : 'Copy failed');
    }
  };

  const openWhatsapp = () => {
    if (!m) return;
    const digits = (m.phone || '').replace(/\D/g, '');
    const url = `https://wa.me/${digits || WHATSAPP_NUMBER}`;
    window.open(url, '_blank', 'noopener');
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto bg-paper"
        data-testid="mother-detail-drawer"
      >
        <SheetHeader className="mb-4">
          <SheetTitle className="font-heading text-xl text-ink text-left">
            {lang === 'zh' ? '妈妈档案详情' : 'Mother Profile Detail'}
          </SheetTitle>
        </SheetHeader>

        {loading || !m ? (
          <div className="py-12 text-center text-ink-tertiary text-sm">{t('loading')}</div>
        ) : (
          <div className="space-y-6">
            {/* Identity */}
            <section className="card-base p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-2xl bg-terracotta-light flex items-center justify-center">
                  <User size={16} className="text-terracotta" />
                </div>
                <div>
                  <div className="font-heading text-lg font-semibold text-ink" data-testid="md-name">{m.name}</div>
                  <div className="text-xs text-ink-tertiary">
                    {lang === 'zh' ? '创建' : 'Created'} {formatKL(m.created_at)}
                  </div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2"><Phone size={12} className="text-ink-tertiary" /> <span data-testid="md-phone">{m.phone}</span></div>
                <div className="flex items-center gap-2"><Baby size={12} className="text-ink-tertiary" /> <span data-testid="md-dob">{m.baby_birth_date}</span></div>
                <div className="text-xs text-ink-tertiary">PIN: {m.has_pin ? '✓' : '—'}</div>
                <div className="text-xs text-ink-tertiary">Lang: {m.language_preference}</div>
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={copyLatestSummary}
                  disabled={!subs[0]}
                  data-testid="md-copy-summary"
                  className="btn-outline text-sm disabled:opacity-50"
                >
                  {copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
                  {lang === 'zh' ? '复制 WhatsApp 总结' : 'Copy WhatsApp summary'}
                </button>
                <button onClick={openWhatsapp} data-testid="md-open-whatsapp" className="btn-primary text-sm">
                  <MessageCircle size={14} className="mr-1" /> WhatsApp
                </button>
              </div>
            </section>

            {/* Self-check history */}
            <section data-testid="md-selfchecks">
              <h3 className="font-heading text-sm font-semibold text-ink mb-2 flex items-center gap-2">
                <ClipboardCheck size={14} className="text-terracotta" />
                {lang === 'zh' ? `Self-check 历史 (${subs.length})` : `Self-check History (${subs.length})`}
              </h3>
              {subs.length === 0 ? (
                <div className="card-base p-4 text-xs text-ink-tertiary">—</div>
              ) : (
                <div className="space-y-2">
                  {subs.map((s) => (
                    <div key={s.id} className="card-base p-3 flex items-center gap-3 flex-wrap" data-testid={`md-sub-${s.id}`}>
                      <RiskBadge level={s.risk_level || 'green'} />
                      <div className="text-xs text-ink-tertiary">{formatKL(s.created_at)}</div>
                      <div className="text-sm text-ink min-w-0 flex-1 truncate">{s.main_concern || '-'}</div>
                      {s.result_type && <div className="text-[10px] text-ink-tertiary">M{s.result_type}</div>}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Milk logs */}
            <section data-testid="md-milklogs">
              <h3 className="font-heading text-sm font-semibold text-ink mb-2 flex items-center gap-2">
                <Droplet size={14} className="text-sage" />
                {lang === 'zh' ? `奶量记录 (${logs.length})` : `Milk Logs (${logs.length})`}
              </h3>
              {logs.length === 0 ? (
                <div className="card-base p-4 text-xs text-ink-tertiary">—</div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {logs.slice(0, 30).map((l) => (
                    <div key={l.id} className="card-base p-3" data-testid={`md-log-${l.id}`}>
                      <div className="text-xs text-ink-tertiary">{formatKLShort(l.date_time)} · {l.method}</div>
                      <div className="text-sm text-ink">
                        L {l.left_ml || 0} · R {l.right_ml || 0} ml
                        {l.duration ? ` · ${l.duration}min` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Case assignments */}
            <section data-testid="md-cases">
              <h3 className="font-heading text-sm font-semibold text-ink mb-2 flex items-center gap-2">
                <Briefcase size={14} className="text-terracotta" />
                {lang === 'zh' ? `案例分配 (${cases.length})` : `Case Assignments (${cases.length})`}
              </h3>
              {cases.length === 0 ? (
                <div className="card-base p-4 text-xs text-ink-tertiary">—</div>
              ) : (
                <div className="space-y-2">
                  {cases.map((c) => {
                    const consultant = consultants.find((x) => x.id === c.assigned_consultant_id);
                    return (
                      <div key={c.id} className="card-base p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`md-case-${c.id}`}>
                        <div className="min-w-0">
                          <div className="text-xs text-ink-tertiary">{formatKL(c.updated_at || c.created_at)}</div>
                          <div className="text-sm text-ink">
                            {consultant?.name || (lang === 'zh' ? '未分配' : 'Unassigned')}
                          </div>
                        </div>
                        <span className="text-[10px] px-2 py-1 rounded-full bg-sage-light text-sage font-semibold">
                          {t(`status_${c.status}`)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
